import * as Tone from 'tone';
import { AppEvent } from "@/constants";
import { AudioComponent } from "./AudioComponent";
import { EffectsPlayer, BackgroundPlayer, LoopPlayer } from "@audio/index";
export class AudioScene extends AudioComponent {
    /**
     * @param {AudioSceneProps} {id, path, config}
     *  id - the id of this scene
     *  path - the path to the audio files for this scene
     *  config - the configuration data for this scene
     */
    constructor({ id, path, config }) {
        super(config.name);
        this._connected = false;
        this.tracks = [];
        this.players = new Map();
        // Debug this
        this.debug = true;
        this.id = id;
        this.path = path;
        this.sceneData = config;
        // Create background player or sampler as needed
        const { background, tracks, effects } = this.sceneData;
        if (background || effects) {
            // Connect directly to the main output
            this.playerOutput = new Tone.Gain();
            // Background player
            if (background) {
                const bgPlayer = new BackgroundPlayer({
                    name: `--[ ${this.name}: background ]`,
                    path,
                    config: background,
                    input: this.playerOutput,
                }).connect(this.playerOutput);
                // this.players.push(bgPlayer);
                this.players.set("background", bgPlayer);
            }
            // Effects player (sound effects)
            if (effects) {
                const effectsPlayer = new EffectsPlayer({
                    name: `--[ ${this.name}: effects ]`,
                    path,
                    config: effects,
                    input: this.playerOutput,
                }).connect(this.playerOutput);
                // this.players.push(effectsPlayer);
                this.players.set("effects", effectsPlayer);
            }
        }
        // Tracks sampler
        if (tracks) {
            this.loopOutput = new Tone.Gain();
            // We only want to work with scene-specific tracks
            // which will not have a group - this will play all of them
            const loopPlayerConfig = {
                tracks: tracks.filter(track => !track.group),
                volume: config.loopsVolume || -12, // TODO: move into the data config files?
            };
            const loopPlayer = new LoopPlayer({
                name: `--[ ${this.name}: loops ]`,
                path,
                config: loopPlayerConfig,
                input: this.loopOutput
            });
            // Watch for the samplers being loaded and pass up the chain
            loopPlayer.once(AppEvent.LOAD_COMPLETE, () => {
                // console.log(`Loop player for ${this.name} loaded`);
                this.emit(AppEvent.LOAD_COMPLETE, this);
            });
            if (config.delay) {
                const { time, feedback } = config.delay;
                this.delay = new Tone.FeedbackDelay(time, feedback);
                loopPlayer.connect(this.delay);
                this.delay.connect(this.loopOutput);
            }
            else {
                loopPlayer.connect(this.loopOutput);
            }
            // this.players.push(loopPlayer);
            this.players.set("loops", loopPlayer);
        }
    }
    /**
     * Connect the output of this scene to another audio nod
     * @param input - the audio node to connect to
     */
    connect(input = null) {
        var _a, _b;
        // Connect up loops which will crossfade
        (_a = this.loopOutput) === null || _a === void 0 ? void 0 : _a.connect(input);
        // Connect the players separately
        (_b = this.playerOutput) === null || _b === void 0 ? void 0 : _b.connect(input);
        this._connected = true;
    }
    /**
     * Enter this scene
     */
    enterScene() {
        ["background", "loops", "effects"].forEach(playerName => {
            if (this.players.has(playerName)) {
                const player = this.players.get(playerName);
                player.enterScene(this.sceneData); // player will call super() with the correct volume
            }
        });
        // Check for presence of players, and enter the scene
        // if (this.sceneData.resetTransport) {
        //     console.log("Resetting transport");
        //     Tone.Transport.start(Tone.immediate(), "0:0:0");
        //     setTimeout(() => console.log(Tone.Transport.position), 100);
        // }
    }
    /**
     * Leave this scene
     */
    leaveScene() {
        this.players.forEach(player => player.exitScene(this.sceneData));
    }
    /**
     * Stop all tracks in this scene
     */
    cleanUp(dispose = false) {
        this.players.forEach(player => player.stopAll());
        this.disconnect(dispose);
    }
    /**
    * Disconnect the output of this scene from another audio node
    */
    disconnect(dispose = false) {
        var _a, _b;
        (_a = this.loopOutput) === null || _a === void 0 ? void 0 : _a.disconnect();
        (_b = this.playerOutput) === null || _b === void 0 ? void 0 : _b.disconnect();
        // Don't disconnect players unless exiting the world
        this.players.forEach(player => {
            if (dispose) {
                player.disconnect();
            }
            else {
                player.stopAll();
            }
        });
        this._connected = false;
    }
    // GETTERS
    get name() {
        return this.sceneData.name;
    }
    get connected() {
        return this._connected;
    }
    log(...args) {
        if (this.debug) {
            console.log(...args);
        }
    }
    toString() {
        return `
        ---------------------------------
        | AudioScene: ${this.name}
        |--------------------------------
        | id: ${this.id}
        | path: ${this.path}
        | ${this.tracks ? `${this.tracks.length} track${this.tracks.length === 1 ? "" : "s"}` : "no tracks"}
        | ${this.players ? `${this.players.values.length} player${this.players.values.length === 1 ? "" : "s"}` : "no players"}
        | ${this.connected ? "CONNECTED" : "not connected"}
        --------------------------------`;
    }
}
